import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Phone, Mail, MessageCircle, ChevronDown, ChevronUp, User, CreditCard, Users, Target, Stethoscope, FileText, Activity, Heart, BookOpen, Save, Edit, X, ClipboardList, Calendar, Plus, Trash2, Label } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientDashboard = () => {
  const { user } = useAuth();
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [showMembershipDetails, setShowMembershipDetails] = useState(false);
  const [editingReminders, setEditingReminders] = useState(false);
  const [reminders, setReminders] = useState('');
  const [savingReminders, setSavingReminders] = useState(false);
  const [visitLogs, setVisitLogs] = useState([]);
  const [showAllVisits, setShowAllVisits] = useState(false);
  
  // Health Goals editing state
  const [editingHealthGoals, setEditingHealthGoals] = useState(false);
  const [healthGoals, setHealthGoals] = useState([]);
  const [savingHealthGoals, setSavingHealthGoals] = useState(false);

  // Personal Info editing state
  const [editingPersonalInfo, setEditingPersonalInfo] = useState(false);
  const [personalInfoData, setPersonalInfoData] = useState({});
  const [savingPersonalInfo, setSavingPersonalInfo] = useState(false);

  const calculateAge = (birthdate) => {
    const today = new Date();
    const birth = new Date(birthdate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  // Warn user about unsaved changes when editing reminders
  useEffect(() => {
    const hasUnsavedChanges = editingReminders && reminders !== (patient?.reminders_to_self || '');
    
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    if (hasUnsavedChanges) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [editingReminders, reminders, patient]);

  // Helper function to display full name
  const getFullName = (identifying_data) => {
    if (!identifying_data) return 'Unknown';
    const first = identifying_data.first_name || '';
    const middle = identifying_data.middle_name || '';
    const last = identifying_data.last_name || '';
    const newFormat = `${first} ${middle} ${last}`.trim();
    return newFormat || identifying_data.full_name || 'Unknown';
  };


  const fetchPatientData = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API}/patients/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPatient(response.data);
      setReminders(response.data.reminders_to_self || '');
      setHealthGoals(response.data.health_goals || []);
      
      // Fetch visit logs
      try {
        const visitLogsResponse = await axios.get(`${API}/patients/${response.data.id}/visit-logs`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        setVisitLogs(visitLogsResponse.data || []);
      } catch (logError) {
        setVisitLogs([]);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching patient data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatientData();
  }, [fetchPatientData]);


  const handleSaveReminders = async () => {
    setSavingReminders(true);
    try {
      await axios.put(
        `${API}/patients/me/reminders`,
        { reminders_to_self: reminders },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Reminders saved successfully');
      setEditingReminders(false);
      fetchPatientData();
    } catch (error) {
      console.error('Error saving reminders:', error);
      toast.error('Failed to save reminders');
    } finally {
      setSavingReminders(false);
    }
  };

  const handleCancelEdit = () => {
    setReminders(patient?.reminders_to_self || '');
    setEditingReminders(false);
  };

  // Health Goals functions
  const handleAddHealthGoal = () => {
    setHealthGoals([
      ...healthGoals,
      { id: Date.now().toString(), category: '', description: '' }
    ]);
  };

  const handleUpdateHealthGoal = (index, field, value) => {
    const updated = [...healthGoals];
    updated[index] = { ...updated[index], [field]: value };
    setHealthGoals(updated);
  };

  const handleRemoveHealthGoal = (index) => {
    setHealthGoals(healthGoals.filter((_, i) => i !== index));
  };

  const handleSaveHealthGoals = async () => {
    // Validate that all goals have category and description
    const invalidGoal = healthGoals.find(goal => !goal.category.trim() || !goal.description.trim());
    if (invalidGoal) {
      toast.error('Please fill in both category and description for all goals');
      return;
    }

    setSavingHealthGoals(true);
    try {
      await axios.put(
        `${API}/patients/me/health-goals`,
        { health_goals: healthGoals },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Health goals saved successfully');
      setEditingHealthGoals(false);
      fetchPatientData();
    } catch (error) {
      console.error('Error saving health goals:', error);
      toast.error('Failed to save health goals');
    } finally {
      setSavingHealthGoals(false);
    }
  };

  const handleCancelHealthGoalsEdit = () => {
    setHealthGoals(patient?.health_goals || []);
    setEditingHealthGoals(false);
  };

  // Personal Info editing functions
  const startEditPersonalInfo = () => {
    const id = patient.identifying_data;
    setPersonalInfoData({
      nickname: id.nickname || '',
      pronouns: id.pronouns || '',
      birthplace: id.birthplace || '',
      home_address: id.home_address || '',
      occupation: id.occupation || '',
      employer_name: id.employer_name || '',
      work_address: id.work_address || '',
      mobile_number: id.mobile_number || '',
      emergency_contact_name: id.emergency_contact_name || id.emergency_contact?.name || '',
      emergency_contact_relationship: id.emergency_contact_relationship || id.emergency_contact?.relationship || '',
      emergency_contact_phone: id.emergency_contact_phone || id.emergency_contact?.phone || ''
    });
    setEditingPersonalInfo(true);
  };

  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfoData(prev => ({ ...prev, [field]: value }));
  };

  const handleSavePersonalInfo = async () => {
    // Validate mobile number if provided
    if (personalInfoData.mobile_number && !/^(\+639|09)\d{9}$/.test(personalInfoData.mobile_number)) {
      toast.error('Invalid mobile number format. Use +639XXXXXXXXX or 09XXXXXXXXX');
      return;
    }
    // Validate emergency contact phone if provided
    if (personalInfoData.emergency_contact_phone && !/^(\+639|09)\d{9}$/.test(personalInfoData.emergency_contact_phone)) {
      toast.error('Invalid emergency contact phone format. Use +639XXXXXXXXX or 09XXXXXXXXX');
      return;
    }

    setSavingPersonalInfo(true);
    try {
      await axios.put(
        `${API}/patients/me/identifying-data`,
        personalInfoData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Personal information updated successfully');
      setEditingPersonalInfo(false);
      fetchPatientData();
    } catch (error) {
      console.error('Error saving personal info:', error);
      toast.error(error.response?.data?.detail || 'Failed to save personal information');
    } finally {
      setSavingPersonalInfo(false);
    }
  };

  const handleCancelPersonalInfoEdit = () => {
    setEditingPersonalInfo(false);
    setPersonalInfoData({});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-700"></div>
          <p className="mt-4 text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-600">No patient data found</p>
        </div>
      </div>
    );
  }

  const getMembershipBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="badge-active" data-testid="membership-status-active">Active</Badge>;
      case 'inactive':
        return <Badge className="badge-inactive" data-testid="membership-status-inactive">Inactive</Badge>;
      case 'expiring_soon':
        return <Badge className="badge-expiring" data-testid="membership-status-expiring">Expiring Soon</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const getMembershipWarning = (status) => {
    if (status === 'expiring_soon') {
      return (
        <div className="mt-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <p className="text-sm text-orange-800 font-medium">Your membership will expire soon. Please contact us to renew.</p>
        </div>
      );
    }
    if (status === 'inactive') {
      return (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-800 font-medium">Your membership has expired. Please contact us to renew.</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      {/* Left Wave Decoration */}
      <div className="absolute left-0 top-0 bottom-0 w-1/4 pointer-events-none opacity-30">
        <svg viewBox="0 0 400 1080" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path d="M0 0C150 200 100 400 200 540C300 680 250 880 150 1080H0V0Z" fill="url(#leftGradient)" />
          <defs>
            <linearGradient id="leftGradient" x1="0" y1="0" x2="200" y2="1080" gradientUnits="userSpaceOnUse">
              <stop stopColor="#0EA5E9" />
              <stop offset="1" stopColor="#06B6D4" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Right Wave Decoration */}
      <div className="absolute right-0 top-0 bottom-0 w-1/3 pointer-events-none opacity-30">
        <svg viewBox="0 0 600 1080" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-full">
          <path d="M600 0C450 150 500 350 350 540C200 730 300 900 450 1080H600V0Z" fill="url(#rightGradient)" />
          <defs>
            <linearGradient id="rightGradient" x1="600" y1="0" x2="300" y2="1080" gradientUnits="userSpaceOnUse">
              <stop stopColor="#0284C7" />
              <stop offset="1" stopColor="#0EA5E9" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 pb-20 md:pb-4">
        {/* Header Section - Mobile Optimized */}
        <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6 gap-4">
          {/* Greeting Section */}
          <div className="flex-1" data-testid="dashboard-header">
            {/* Temporary Password Warning */}
            {user?.has_temporary_password && (
              <div className="mb-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg shadow-sm">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg className="w-6 h-6 text-yellow-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-yellow-900 mb-1">⚠️ Security Alert</h3>
                    <p className="text-sm text-yellow-800 mb-2">
                      You&apos;re using a temporary password. Change it now in Settings for your security.
                    </p>
                    <a
                      href="/settings"
                      className="inline-flex items-center px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs font-medium rounded-lg transition-colors"
                    >
                      Change Password →
                    </a>
                  </div>
                </div>
              </div>
            )}
            
            <div className="relative">
              <div className="hidden md:block absolute -left-4 top-0 w-1 h-16 bg-gradient-to-b from-cyan-500 to-blue-500 rounded-full"></div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent mb-2">
                Hi, {patient.identifying_data.nickname}! <span className="hidden md:inline">👋</span>
              </h1>
              <p className="text-sm md:text-base lg:text-lg text-slate-600">Welcome back to your health and well-being dashboard.</p>
            </div>
          </div>

          {/* Personal Info Button - Mobile Optimized */}
          <div className="flex-shrink-0">
            <button
              onClick={() => setShowPersonalInfo(!showPersonalInfo)}
              className="flex items-center gap-2 px-4 py-3 md:px-3 md:py-1.5 bg-white border-2 md:border border-slate-300 rounded-xl md:rounded-lg hover:border-cyan-400 hover:bg-cyan-50 transition-all shadow-sm text-sm w-full md:w-auto justify-center md:justify-start min-h-[44px]"
              title="View your personal information"
            >
              <User size={18} className="text-cyan-700" />
              <span className="font-medium text-slate-700">Personal Info</span>
              {showPersonalInfo ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
        </div>

        {/* Collapsible Personal Information Section */}
        {showPersonalInfo && (
          <div className="mb-6 animate-fadeIn" data-testid="identifying-data-card">
            <div className="card border-2 border-cyan-200 bg-gradient-to-br from-white to-cyan-50">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Your Personal Information</h2>
                <div className="flex items-center gap-2">
                  {!editingPersonalInfo && (
                    <Button
                      onClick={startEditPersonalInfo}
                      className="btn-secondary text-sm"
                      data-testid="edit-personal-info-btn"
                    >
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                  )}
                  <button
                    onClick={() => { setShowPersonalInfo(false); setEditingPersonalInfo(false); }}
                    className="text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <ChevronUp size={20} />
                  </button>
                </div>
              </div>
              
              {!editingPersonalInfo ? (
                /* View Mode */
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="flex justify-center md:col-span-1">
                    {patient.identifying_data.profile_photo ? (
                      <img
                        src={patient.identifying_data.profile_photo}
                        alt={getFullName(patient.identifying_data)}
                        className="w-24 h-24 rounded-full object-cover border-4 border-cyan-200 shadow-lg"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 border-4 border-white shadow-lg flex items-center justify-center">
                        <span className="text-3xl font-bold text-white">
                          {getFullName(patient.identifying_data).charAt(0) || '?'}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="md:col-span-3 grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Full Name</p>
                      <p className="text-slate-900 font-medium">{getFullName(patient.identifying_data)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Nickname</p>
                      <p className="text-slate-900">{patient.identifying_data.nickname || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Birthdate</p>
                      <p className="text-slate-900">{patient.identifying_data.birthdate || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Age</p>
                      <p className="text-slate-900">{patient.identifying_data.birthdate ? `${calculateAge(patient.identifying_data.birthdate)} years` : 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Sex Assigned at Birth</p>
                      <p className="text-slate-900">{patient.identifying_data.sex || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Pronouns</p>
                      <p className="text-slate-900">{patient.identifying_data.pronouns || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email Address</p>
                      <p className="text-slate-900">{patient.identifying_data.email || user?.email || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Mobile Number</p>
                      <p className="text-slate-900">{patient.identifying_data.mobile_number || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Birthplace</p>
                      <p className="text-slate-900">{patient.identifying_data.birthplace || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Home Address</p>
                      <p className="text-slate-900">{patient.identifying_data.home_address || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Occupation</p>
                      <p className="text-slate-900">{patient.identifying_data.occupation || 'Not provided'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Employer</p>
                      <p className="text-slate-900">{patient.identifying_data.employer_name || 'Not provided'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Work Address</p>
                      <p className="text-slate-900">{patient.identifying_data.work_address || 'Not provided'}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Emergency Contact</p>
                      <p className="text-slate-900 font-medium">{patient.identifying_data.emergency_contact_name || patient.identifying_data.emergency_contact?.name || 'Not provided'}</p>
                      <p className="text-slate-600 text-sm">{patient.identifying_data.emergency_contact_relationship || patient.identifying_data.emergency_contact?.relationship || ''}</p>
                      <p className="text-slate-600 text-sm">{patient.identifying_data.emergency_contact_phone || patient.identifying_data.emergency_contact?.phone || ''}</p>
                    </div>
                  </div>
                </div>
              ) : (
                /* Edit Mode */
                <div className="space-y-5">
                  {/* Read-only fields notice */}
                  <div className="p-3 bg-slate-100 rounded-lg border border-slate-200">
                    <p className="text-xs text-slate-600">
                      <span className="font-semibold">Name, email, birthdate, and sex</span> can only be changed by your care team or admin. All other fields below are editable.
                    </p>
                  </div>

                  {/* Read-only summary */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="p-2 bg-slate-50 rounded border border-slate-200">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Name</p>
                      <p className="text-sm font-medium text-slate-700">{getFullName(patient.identifying_data)}</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded border border-slate-200">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                      <p className="text-sm font-medium text-slate-700">{patient.identifying_data.email || user?.email}</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded border border-slate-200">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Birthdate</p>
                      <p className="text-sm font-medium text-slate-700">{patient.identifying_data.birthdate || 'Not set'}</p>
                    </div>
                    <div className="p-2 bg-slate-50 rounded border border-slate-200">
                      <p className="text-xs text-slate-500 uppercase tracking-wider">Sex</p>
                      <p className="text-sm font-medium text-slate-700">{patient.identifying_data.sex || 'Not set'}</p>
                    </div>
                  </div>

                  {/* Editable fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Nickname</label>
                      <Input
                        value={personalInfoData.nickname}
                        onChange={(e) => handlePersonalInfoChange('nickname', e.target.value)}
                        placeholder="Your nickname"
                        data-testid="edit-nickname"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Pronouns</label>
                      <select
                        value={personalInfoData.pronouns}
                        onChange={(e) => handlePersonalInfoChange('pronouns', e.target.value)}
                        className="w-full rounded-lg border-slate-200 px-4 py-2.5"
                        data-testid="edit-pronouns"
                      >
                        <option value="">Select...</option>
                        <option value="he/him">he/him</option>
                        <option value="she/her">she/her</option>
                        <option value="they/them">they/them</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Mobile Number</label>
                      <Input
                        value={personalInfoData.mobile_number}
                        onChange={(e) => handlePersonalInfoChange('mobile_number', e.target.value)}
                        placeholder="+639XXXXXXXXX or 09XXXXXXXXX"
                        data-testid="edit-mobile-number"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Birthplace</label>
                      <Input
                        value={personalInfoData.birthplace}
                        onChange={(e) => handlePersonalInfoChange('birthplace', e.target.value)}
                        placeholder="City of birth"
                        data-testid="edit-birthplace"
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Home Address</label>
                      <Input
                        value={personalInfoData.home_address}
                        onChange={(e) => handlePersonalInfoChange('home_address', e.target.value)}
                        placeholder="Your home address"
                        data-testid="edit-home-address"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Occupation</label>
                      <Input
                        value={personalInfoData.occupation}
                        onChange={(e) => handlePersonalInfoChange('occupation', e.target.value)}
                        placeholder="Your occupation"
                        data-testid="edit-occupation"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Employer</label>
                      <Input
                        value={personalInfoData.employer_name}
                        onChange={(e) => handlePersonalInfoChange('employer_name', e.target.value)}
                        placeholder="Company or employer name"
                        data-testid="edit-employer"
                      />
                    </div>
                    <div className="col-span-1 md:col-span-2">
                      <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Work Address</label>
                      <Input
                        value={personalInfoData.work_address}
                        onChange={(e) => handlePersonalInfoChange('work_address', e.target.value)}
                        placeholder="Your work address"
                        data-testid="edit-work-address"
                      />
                    </div>
                  </div>

                  {/* Emergency Contact */}
                  <div>
                    <h3 className="text-sm font-semibold text-slate-700 mb-3 mt-2">Emergency Contact</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Name</label>
                        <Input
                          value={personalInfoData.emergency_contact_name}
                          onChange={(e) => handlePersonalInfoChange('emergency_contact_name', e.target.value)}
                          placeholder="Emergency contact name"
                          data-testid="edit-ec-name"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Relationship</label>
                        <Input
                          value={personalInfoData.emergency_contact_relationship}
                          onChange={(e) => handlePersonalInfoChange('emergency_contact_relationship', e.target.value)}
                          placeholder="e.g., Mother, Spouse"
                          data-testid="edit-ec-relationship"
                        />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1 block">Phone</label>
                        <Input
                          value={personalInfoData.emergency_contact_phone}
                          onChange={(e) => handlePersonalInfoChange('emergency_contact_phone', e.target.value)}
                          placeholder="+639XXXXXXXXX"
                          data-testid="edit-ec-phone"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Save / Cancel */}
                  <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2">
                    <Button
                      onClick={handleCancelPersonalInfoEdit}
                      disabled={savingPersonalInfo}
                      className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm px-5 py-3 md:py-2 rounded-xl md:rounded-lg min-h-[44px] md:min-h-0 w-full sm:w-auto order-2 sm:order-1"
                      data-testid="cancel-personal-info-btn"
                    >
                      <X size={16} className="mr-1" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSavePersonalInfo}
                      disabled={savingPersonalInfo}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white text-sm px-5 py-3 md:py-2 rounded-xl md:rounded-lg min-h-[44px] md:min-h-0 w-full sm:w-auto order-1 sm:order-2"
                      data-testid="save-personal-info-btn"
                    >
                      <Save size={16} className="mr-1" />
                      {savingPersonalInfo ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Membership Status and Care Team - Mobile Optimized */}
        <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Membership Status */}
          <div>
            <div className="card border-2 border-cyan-200 bg-gradient-to-br from-white to-cyan-50 shadow-lg hover:shadow-xl transition-shadow h-full" data-testid="membership-status-card">
              <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-cyan-100">
                <h2 className="text-lg md:text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <CreditCard size={20} className="text-cyan-600" />
                  Membership Status
                </h2>
                <button
                  onClick={() => setShowMembershipDetails(!showMembershipDetails)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-cyan-700 hover:text-cyan-900 hover:bg-cyan-100 rounded-lg transition-colors"
                >
                  {showMembershipDetails ? (
                    <>
                      <ChevronUp size={16} />
                      <span className="hidden sm:inline">Hide Details</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} />
                      <span className="hidden sm:inline">Show Details</span>
                    </>
                  )}
                </button>
              </div>

              {/* Compact View - Always Visible */}
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-slate-600">Status:</span>
                {getMembershipBadge(patient.membership_status.status)}
              </div>
              
              {getMembershipWarning(patient.membership_status.status)}

              {/* Expanded Details - Collapsible */}
              {showMembershipDetails && (
                <div className="mt-4 pt-4 border-t border-cyan-100 space-y-3 animate-fadeIn">
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Start Date</p>
                    <p className="text-slate-900">{patient.membership_status.start_date}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Expiry Date</p>
                    <p className="text-slate-900">{patient.membership_status.expiry_date}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Onboarding</p>
                    <p className="text-slate-900 capitalize">{patient.membership_status.onboarding_status}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Comprehensive Health Interview</p>
                    <p className="text-slate-900 capitalize">{patient.membership_status.health_interview_status}</p>
                  </div>
                  {patient.membership_status.health_interview_date && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">First Patient Encounter</p>
                      <p className="text-slate-900">{patient.membership_status.health_interview_date}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Care Team */}
          <div>
            <div className="card border-2 border-blue-200 bg-gradient-to-br from-white to-blue-50 shadow-lg hover:shadow-xl transition-shadow h-full" data-testid="care-team-card">
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-blue-100 flex items-center gap-2">
                <Users size={20} className="text-blue-600" />
                Your Care Team
              </h2>
              
              <div className="space-y-4">
                {patient.care_team
                  .sort((a, b) => {
                    // System users (provider/admin) come first, external members last
                    if (a.user_type === 'external' && b.user_type !== 'external') return 1;
                    if (a.user_type !== 'external' && b.user_type === 'external') return -1;
                    return 0;
                  })
                  .map((member) => (
                  <div key={member.id} className="flex items-start space-x-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
                    {/* Show profile photo only for system users (provider/admin) */}
                    {member.user_type && member.user_type !== 'external' && (
                      <div className="flex-shrink-0">
                        {member.avatar ? (
                          <img
                            src={member.avatar}
                            alt={member.name}
                            className="w-12 h-12 rounded-full object-cover border-2 border-cyan-200"
                          />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-cyan-600 flex items-center justify-center border-2 border-white shadow">
                            <span className="text-lg font-bold text-white">
                              {member.name?.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{member.name}</h3>
                        {member.user_type === 'external' && (
                          <span className="px-2 py-0.5 text-xs bg-slate-200 text-slate-600 rounded-full">External</span>
                        )}
                      </div>
                      <p className="text-sm text-slate-600 mb-2">{member.role}</p>
                      
                      {/* Contact Details Display */}
                      {member.user_type && member.user_type !== 'external' && (
                        <div className="space-y-1 mb-3">
                          {member.clinic_hours && (
                            <p className="text-xs text-slate-600">
                              <span className="font-semibold">Clinic Hours:</span> {member.clinic_hours}
                            </p>
                          )}
                          {member.phone && (
                            <p className="text-xs text-slate-600">
                              <span className="font-semibold">Mobile/Viber:</span> {member.phone}
                            </p>
                          )}
                          {member.email && (
                            <p className="text-xs text-slate-600">
                              <span className="font-semibold">Email:</span> {member.email}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Show contact buttons only for system users */}
                      {member.user_type && member.user_type !== 'external' && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {member.phone && (
                            <a
                              href={`sms:${member.phone}`}
                              className="inline-flex items-center justify-center px-5 py-3 md:px-4 md:py-2 text-sm font-semibold text-white bg-blue-600 rounded-xl md:rounded-lg hover:bg-blue-700 transition-colors shadow-sm min-h-[44px] flex-1 md:flex-initial"
                            >
                              <MessageCircle size={18} className="mr-2" />
                              SMS
                            </a>
                          )}
                          {member.email && (
                            <a
                              href={`mailto:${member.email}`}
                              className="inline-flex items-center justify-center px-5 py-3 md:px-4 md:py-2 text-sm font-semibold text-white bg-cyan-600 rounded-xl md:rounded-lg hover:bg-cyan-700 transition-colors shadow-sm min-h-[44px] flex-1 md:flex-initial"
                            >
                              <Mail size={18} className="mr-2" />
                              Email
                            </a>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reminders To Self - Private Section */}
        <div className="mb-4 md:mb-6">
          <div className="card border-2 border-green-200 bg-gradient-to-br from-green-50/50 to-white shadow-lg">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 pb-2 border-b-2 border-green-200">
              <h2 className="text-lg md:text-xl font-semibold text-green-900 flex items-center gap-2">
                <BookOpen size={20} className="text-green-600" />
                Reminders To Self
              </h2>
              {!editingReminders && (
                <Button
                  onClick={() => setEditingReminders(true)}
                  className="bg-green-600 hover:bg-green-700 text-white text-sm md:text-xs px-4 py-3 md:px-3 md:py-1.5 rounded-xl md:rounded-lg min-h-[44px] md:min-h-0 w-full sm:w-auto"
                >
                  <Edit size={16} className="mr-1" />
                  Edit
                </Button>
              )}
            </div>

            <div className="bg-white/50 p-4 rounded-lg border border-green-100">
              <p className="text-xs text-green-700 mb-3 flex items-center gap-1">
                <span className="font-semibold">🔒 Private:</span> Only you can see this. Not visible to providers or admins.
              </p>

              {editingReminders ? (
                <div className="space-y-3">
                  <Textarea
                    value={reminders}
                    onChange={(e) => setReminders(e.target.value)}
                    placeholder="Write your personal health notes, reminders, questions for your doctor, or anything you want to remember..."
                    className="w-full min-h-[200px] text-sm"
                  />
                  <div className="flex flex-col sm:flex-row gap-2 justify-end">
                    <Button
                      onClick={handleCancelEdit}
                      disabled={savingReminders}
                      className="bg-slate-200 hover:bg-slate-300 text-slate-700 text-sm md:text-xs px-5 py-3 md:px-4 md:py-2 rounded-xl md:rounded-lg min-h-[44px] md:min-h-0 w-full sm:w-auto order-2 sm:order-1"
                    >
                      <X size={16} className="mr-1" />
                      Cancel
                    </Button>
                    <Button
                      onClick={handleSaveReminders}
                      disabled={savingReminders}
                      className="bg-green-600 hover:bg-green-700 text-white text-sm md:text-xs px-5 py-3 md:px-4 md:py-2 rounded-xl md:rounded-lg min-h-[44px] md:min-h-0 w-full sm:w-auto order-1 sm:order-2"
                    >
                      <Save size={16} className="mr-1" />
                      {savingReminders ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap min-h-[100px]">
                  {reminders || 'No reminders yet. Click Edit to add your personal notes.'}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Visit Log - View Only for Patient */}
        <div className="mb-4 md:mb-6">
          <div className="card border-2 border-purple-200 bg-gradient-to-br from-purple-50/50 to-white shadow-lg">
            <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-purple-200">
              <h2 className="text-lg md:text-xl font-semibold text-purple-900 flex items-center gap-2">
                <ClipboardList size={20} className="text-purple-600" />
                Visit Log
              </h2>
              <span className="text-xs text-purple-600 bg-purple-100 px-3 py-1 rounded-full">View Only</span>
            </div>

            {visitLogs.length === 0 ? (
              <div className="text-center py-8">
                <ClipboardList size={48} className="mx-auto text-purple-300 mb-3" />
                <p className="text-slate-500 text-sm">No visit logs yet. Your provider will add notes after consultations.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Show first 3 visits by default */}
                {visitLogs.slice(0, showAllVisits ? visitLogs.length : 3).map((log, index) => (
                  <div 
                    key={log.id} 
                    className="p-4 bg-white rounded-lg border-2 border-purple-100 hover:border-purple-200 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-purple-600" />
                        <span className="text-sm font-semibold text-purple-900">
                          {new Date(log.date).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}
                        </span>
                        {index === 0 && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">
                            Most Recent
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mb-2">
                      <p className="text-xs text-slate-500">Provider</p>
                      <p className="text-sm font-medium text-slate-700">{log.provider_name}</p>
                    </div>

                    <div className="mt-3 pt-3 border-t border-purple-100">
                      <p className="text-xs text-slate-500 mb-1">Visit Notes</p>
                      <div className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap bg-slate-50 p-3 rounded">
                        {log.notes}
                      </div>
                    </div>

                    <div className="mt-2 text-xs text-slate-400">
                      Last updated: {new Date(log.updated_at).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                ))}

                {/* Show more/less button if there are more than 3 visits */}
                {visitLogs.length > 3 && (
                  <button
                    onClick={() => setShowAllVisits(!showAllVisits)}
                    className="w-full py-3 text-sm font-medium text-purple-700 hover:text-purple-900 hover:bg-purple-50 rounded-lg transition-colors flex items-center justify-center gap-2 border-2 border-purple-200 hover:border-purple-300"
                  >
                    {showAllVisits ? (
                      <>
                        <ChevronUp size={18} />
                        Show Recent Visits Only ({visitLogs.slice(0, 3).length} of {visitLogs.length})
                      </>
                    ) : (
                      <>
                        <ChevronDown size={18} />
                        Show All Visits ({visitLogs.length} total)
                      </>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Health Goals and Problem List - Mobile Optimized */}
        <div className="mb-4 md:mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
          {/* Health Goals - Editable by Patient */}
          <div className="card border-2 border-green-200 bg-gradient-to-br from-white to-green-50 shadow-lg hover:shadow-xl transition-shadow h-full" data-testid="health-goals-card">
            <div className="flex items-center justify-between mb-4 pb-2 border-b-2 border-green-100">
              <h2 className="text-lg md:text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Target size={20} className="text-green-600" />
                Your Health Goals
              </h2>
              {!editingHealthGoals ? (
                <Button 
                  onClick={() => setEditingHealthGoals(true)} 
                  className="btn-secondary text-sm"
                >
                  <Edit size={16} className="mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button 
                    onClick={handleAddHealthGoal} 
                    className="btn-secondary text-sm"
                  >
                    <Plus size={16} className="mr-1" />
                    <span className="hidden sm:inline">Add</span>
                  </Button>
                  <Button 
                    onClick={handleCancelHealthGoalsEdit} 
                    className="btn-ghost text-red-600 text-sm"
                  >
                    <X size={16} />
                  </Button>
                </div>
              )}
            </div>
            
            {!editingHealthGoals ? (
              // View Mode
              healthGoals.length === 0 ? (
                <div className="text-center py-4">
                  <p className="text-slate-500 text-sm mb-3">No health goals set yet.</p>
                  <Button 
                    onClick={() => {
                      setEditingHealthGoals(true);
                      handleAddHealthGoal();
                    }} 
                    className="btn-secondary text-sm"
                  >
                    <Plus size={16} className="mr-1" />
                    Add Your First Goal
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {healthGoals.map((goal) => (
                    <div key={goal.id} className="p-4 bg-gradient-to-r from-cyan-50 to-sky-50 rounded-lg border border-cyan-100">
                      <div className="flex items-start justify-between mb-2">
                        <span className="inline-block px-3 py-1 text-xs font-semibold text-cyan-700 bg-white rounded-full border border-cyan-200">
                          {goal.category}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{goal.description}</p>
                    </div>
                  ))}
                </div>
              )
            ) : (
              // Edit Mode
              <div className="space-y-4">
                {healthGoals.length === 0 ? (
                  <p className="text-slate-500 text-sm text-center py-4">Click &ldquo;Add&rdquo; to create a health goal</p>
                ) : (
                  healthGoals.map((goal, index) => (
                    <div key={goal.id} className="p-4 bg-white rounded-lg border-2 border-green-200">
                      <div className="flex justify-between items-start mb-3">
                        <span className="text-sm font-semibold text-slate-700">Goal {index + 1}</span>
                        <Button
                          onClick={() => handleRemoveHealthGoal(index)}
                          className="btn-ghost text-red-500 p-1 h-auto"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Category</label>
                          <Input
                            value={goal.category}
                            onChange={(e) => handleUpdateHealthGoal(index, 'category', e.target.value)}
                            placeholder="e.g., Fitness, Nutrition, Mental Health"
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 mb-1 block">Description</label>
                          <Textarea
                            value={goal.description}
                            onChange={(e) => handleUpdateHealthGoal(index, 'description', e.target.value)}
                            placeholder="Describe your health goal..."
                            rows={2}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
                
                {/* Save/Cancel buttons */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveHealthGoals}
                    disabled={savingHealthGoals}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save size={16} className="mr-2" />
                    {savingHealthGoals ? 'Saving...' : 'Save Goals'}
                  </Button>
                  <Button
                    onClick={handleCancelHealthGoalsEdit}
                    variant="outline"
                    className="border-slate-300"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Problem List */}
          <div className="card border-2 border-rose-200 bg-gradient-to-br from-white to-rose-50 shadow-lg hover:shadow-xl transition-shadow h-full" data-testid="problem-list-card">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-rose-100 flex items-center gap-2">
              <Stethoscope size={20} className="text-rose-600" />
              Problem List
            </h2>
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                {patient.health_record.problem_list || 'No problems listed yet.'}
              </p>
            </div>
          </div>
        </div>

        {/* Health Record (Accordion) - Full Width */}
        <div className="mb-4 md:mb-6">
          <div className="card border-2 border-purple-200 bg-gradient-to-br from-white to-purple-50 shadow-lg hover:shadow-xl transition-shadow" data-testid="health-record-card">
            <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-4 pb-2 border-b-2 border-purple-100 flex items-center gap-2">
              <FileText size={20} className="text-purple-600" />
              Your Health Record
            </h2>
              
              <div className="space-y-6">
                {/* Clinical History Section */}
                <div className="p-5 rounded-xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 to-white shadow-sm">
                  <h3 className="text-lg font-semibold text-indigo-900 mb-4 pb-2 border-b-2 border-indigo-200 flex items-center gap-2">
                    <Activity size={18} className="text-indigo-600" />
                    Clinical History
                  </h3>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="birth_history">
                      <AccordionTrigger>Birth History</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.birth_history || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="immunization_history">
                      <AccordionTrigger>Immunization History</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.immunization_history || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="past_medical_history">
                      <AccordionTrigger>Past Medical History</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.past_medical_history || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="past_surgical_history">
                      <AccordionTrigger>Past Surgical History</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.past_surgical_history || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="obgyne_menstrual_history">
                      <AccordionTrigger>OB-Gyne & Menstrual History</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.obgyne_menstrual_history || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="sexual_history">
                      <AccordionTrigger>Sexual History</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.sexual_history || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="medications">
                      <AccordionTrigger>Medication History</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.medications || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="allergies">
                      <AccordionTrigger>Known Allergies</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.allergies || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Lifestyle Summary Section */}
                <div className="p-5 rounded-xl border-2 border-orange-200 bg-gradient-to-br from-orange-50/50 to-white shadow-sm">
                  <h3 className="text-lg font-semibold text-orange-900 mb-4 pb-2 border-b-2 border-orange-200 flex items-center gap-2">
                    <Heart size={18} className="text-orange-600" />
                    Lifestyle Summary
                  </h3>
                  <Accordion type="single" collapsible className="w-full">
                    <AccordionItem value="nutrition">
                      <AccordionTrigger>Nutrition</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.nutrition || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="physical_activity">
                      <AccordionTrigger>Physical Activity</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.physical_activity || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="sleep">
                      <AccordionTrigger>Sleep</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.sleep || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="substance_use">
                      <AccordionTrigger>Substance Use</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.substance_use || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="relationships">
                      <AccordionTrigger>Relationships</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.relationships || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="stressors">
                      <AccordionTrigger>Stressors</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.stressors || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="resiliency_practices">
                      <AccordionTrigger>Resiliency Practices</AccordionTrigger>
                      <AccordionContent>
                        <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-wrap">
                          {patient.health_record.resiliency_practices || 'No information available yet.'}
                        </p>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              </div>

            </div>
          </div>
      </div>
    </div>
  );
};

export default PatientDashboard;